/**
 * Payslip presentation helpers — display only. Does not alter payroll calculations.
 */

function normLabel(value) {
  return String(value || "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, " ");
}

function lineCode(item) {
  const meta = item?.meta && typeof item.meta === "object" ? item.meta : {};
  for (const key of ["code", "component_code", "componentCode", "salary_component_code"]) {
    const raw = item?.[key] ?? meta[key];
    if (raw) return normLabel(raw).replace(/-/g, "_");
  }
  return "";
}

function lineLabel(item) {
  for (const key of ["component", "name", "label", "component_name"]) {
    const raw = item?.[key];
    if (raw) return normLabel(raw);
  }
  return "";
}

function normCode(item) {
  return lineCode(item) || String(item?.code || "").trim().toUpperCase();
}

const SECONDARY_EARNING_ORDER = [
  "DA",
  "DEARNESS",
  "CONVEYANCE",
  "MEDICAL",
  "SPECIAL",
  "SPECIAL_ALLOWANCE",
  "SPA",
  "LTA",
  "OVERTIME",
  "OT",
  "BONUS",
  "REIMBURSEMENT",
];

export function sumAmounts(items = []) {
  return items.reduce((s, i) => s + (Number(i?.amount) || 0), 0);
}

/** True when a payslip label/value pair should be shown (not blank / placeholder). */
export function hasPayslipFieldValue(v) {
  if (v == null || v === undefined) return false;
  if (typeof v === "number") return Number.isFinite(v);
  const s = String(v).trim();
  return s.length > 0 && s !== "—" && s !== "-" && s !== "–";
}

function payslipFmtNum(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  return n.toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}

/**
 * Employee info grid rows (left + right columns). Only fields with data are included.
 */
export function buildPayslipInfoTableRows({ emp = {}, org = {}, att = {}, empName = "" }) {
  const workingDays = att.total_working_days ?? att.calendar_days;
  const payableDays = att.payable_days;
  const lopRaw = att.lop_units ?? att.lop_days;
  const accountNo = emp.bank_account_number || emp.account_number;

  const left = [
    ["Employee Name", empName],
    ["Employee ID", emp.employee_code],
    ["Designation", emp.designation],
    ["Department", emp.department],
    ["Work Location", emp.location || org.city],
    ["PAN", emp.pan],
    ["PF Number", emp.pf_account_number],
  ].filter(([, v]) => hasPayslipFieldValue(v));

  const right = [
    ["Working Days", workingDays != null && workingDays !== "" ? payslipFmtNum(workingDays) : null],
    ["Payable Days", payableDays != null && payableDays !== "" ? payslipFmtNum(payableDays) : null],
    ["LOP Days", lopRaw != null && lopRaw !== "" ? payslipFmtNum(lopRaw) : null],
    ["Bank", emp.bank_name],
    ["Account No.", accountNo],
    ["UAN", emp.uan],
  ].filter(([, v]) => hasPayslipFieldValue(v));

  const rowCount = Math.max(left.length, right.length);
  const rows = [];
  for (let i = 0; i < rowCount; i += 1) {
    rows.push({
      left: left[i] || null,
      right: right[i] || null,
    });
  }
  return rows;
}

export function maskBankAccount(raw) {
  if (!raw) return null;
  const s = String(raw).replace(/\s/g, "");
  if (s.length <= 4) return "****";
  return `XXXX XXXX ${s.slice(-4)}`;
}

function isBasicEarning(item) {
  const code = lineCode(item) || normCode(item).replace(/-/g, "_");
  const name = lineLabel(item);
  if (["BASIC", "BASIC_SALARY", "BASIC_PAY", "BASE", "BASE_SALARY", "BASIC_SAL"].includes(code)) {
    return true;
  }
  if (code.startsWith("BASIC") || code.endsWith("_BASIC")) return true;
  if (name && /\bBASIC\b/.test(name)) return true;
  return name.startsWith("BASIC ") || name.startsWith("BASIC PAY");
}

function isHraEarning(item) {
  const code = lineCode(item) || normCode(item).replace(/-/g, "_");
  const name = lineLabel(item);
  if (["HRA", "HOUSE_RENT", "HOUSE_RENT_ALLOWANCE", "HR_ALLOWANCE", "HOUSE_RENTAL"].includes(code)) {
    return true;
  }
  if (code.startsWith("HRA") || code.includes("HOUSE_RENT")) return true;
  return name === "HRA" || /^HRA\b/.test(name) || name.includes("HOUSE RENT");
}

function primaryEarningRank(item) {
  if (isBasicEarning(item)) return 0;
  if (isHraEarning(item)) return 1;
  return 2;
}

function secondaryEarningRank(item) {
  const code = lineCode(item) || normCode(item);
  const name = lineLabel(item);
  for (let i = 0; i < SECONDARY_EARNING_ORDER.length; i += 1) {
    const token = SECONDARY_EARNING_ORDER[i];
    if (code === token || code.startsWith(`${token}_`)) return i;
    if (token === "SPECIAL" && name.includes("SPECIAL")) return i;
    if (token === "CONVEYANCE" && name.includes("CONVEYANCE")) return i;
    if (token === "MEDICAL" && name.includes("MEDICAL")) return i;
    if ((token === "OVERTIME" || token === "OT") && (name.includes("OVERTIME") || name === "OT")) {
      return i;
    }
  }
  return SECONDARY_EARNING_ORDER.length;
}

/** Standard payslip order: Basic, HRA, then all other earnings (stable within each group). */
export function sortEarningsForDisplay(rows = []) {
  return rows
    .map((item, index) => ({
      item,
      index,
      primary: primaryEarningRank(item),
      secondary: secondaryEarningRank(item),
    }))
    .sort(
      (a, b) =>
        a.primary - b.primary ||
        a.secondary - b.secondary ||
        a.index - b.index
    )
    .map(({ item }) => item);
}

/** All real payroll earning lines — no synthetic uplift rows. */
export function collectActualEarnings(earnings = [], sections = {}) {
  const rows = [];
  const push = (items) => {
    for (const item of items || []) {
      const amt = Number(item?.amount);
      if (!Number.isFinite(amt) || amt === 0) continue;
      const code = String(item?.code || "").toUpperCase();
      if (code === "OVERTIME" || code === "OT" || code === "FUEL_REIMBURSEMENT") continue;
      rows.push(item);
    }
  };
  push(earnings);
  push(sections?.arrears);
  push(sections?.bonus);
  push(sections?.reimbursements);
  return sortEarningsForDisplay(rows);
}

export function buildAttendancePresentation(att = {}) {
  const paidLeave = att.earned_leave_units ?? att.paid_leave_units ?? null;
  const overtimeHours = att.overtime_hours ?? att.overtime_units ?? null;

  const core = [
    { key: "wd", label: "Working days", value: att.total_working_days },
    { key: "pres", label: "Present days", value: att.present_units },
    { key: "pl", label: "Paid leave", value: paidLeave },
    { key: "lop", label: "LOP days", value: att.lop_units },
    { key: "wo", label: "Week-offs", value: att.week_off_units },
    { key: "hol", label: "Holidays", value: att.holiday_units },
    { key: "pd", label: "Payable days", value: att.payable_days, highlight: true },
  ].filter((r) => r.value != null && r.value !== "");

  const additionalWork = [
    { key: "sw", label: "Sunday worked", value: att.sunday_work_units, unit: "Days" },
    { key: "ww", label: "Week-off worked", value: att.weekoff_work_units, unit: "Days" },
    { key: "hw", label: "Holiday worked", value: att.holiday_work_units, unit: "Days" },
    { key: "prem", label: "Premium units", value: att.premium_work_units, unit: "Units" },
    { key: "od", label: "Over-duty days", value: att.over_duty_days, unit: "Days" },
    { key: "ot", label: "Overtime", value: overtimeHours, unit: att.overtime_hours != null ? "Hours" : "Units" },
  ].filter((r) => r.value != null && Number(r.value) > 0);

  return { core, additionalWork };
}

/**
 * Informational salary impact — explains gross vs normal package without duplicating earnings.
 */
export function buildSalaryImpactAnalysis({ grossSalary, salaryPackage = {}, attendance = {} }) {
  const actualGross = Number(grossSalary) || 0;
  const normalGross =
    Number(salaryPackage.full_month_gross_estimated) || Number(salaryPackage.monthly_ctc) || actualGross;
  const difference = actualGross - normalGross;

  const workReasons = [];
  const sw = Number(attendance.sunday_work_units);
  const hw = Number(attendance.holiday_work_units);
  const ww = Number(attendance.weekoff_work_units);
  const prem = Number(attendance.premium_work_units);
  const od = Number(attendance.over_duty_days);
  const ot = Number(attendance.overtime_hours ?? attendance.overtime_units);

  if (sw > 0) workReasons.push({ label: "Sunday worked", detail: `${sw} day(s)` });
  if (ww > 0) workReasons.push({ label: "Week-off worked", detail: `${ww} day(s)` });
  if (hw > 0) workReasons.push({ label: "Holiday worked", detail: `${hw} day(s)` });
  if (prem > 0) workReasons.push({ label: "Premium units", detail: `${prem} unit(s)` });
  if (od > 0) workReasons.push({ label: "Over-duty days", detail: `${od} day(s)` });
  if (ot > 0) workReasons.push({ label: "Overtime", detail: `${ot} hour(s)` });

  const metrics = [];
  if (attendance.payable_days != null && attendance.payable_days !== "") {
    metrics.push({ label: "Payable days", detail: String(attendance.payable_days) });
  }
  if (attendance.total_working_days != null && attendance.total_working_days !== "") {
    metrics.push({ label: "Working days", detail: String(attendance.total_working_days) });
  }
  const wpf = salaryPackage.wage_proration_percent ?? salaryPackage.wage_proration_factor;
  if (wpf != null && wpf !== "") {
    const pct =
      Number(wpf) <= 1 && Number(wpf) > 0
        ? (Number(wpf) * 100).toFixed(2)
        : Number(wpf).toFixed(2);
    metrics.push({ label: "Wage proration factor", detail: `${pct}%` });
  }

  const isHigher = difference > 0.01;
  const isLower = difference < -0.01;
  const show = isHigher || isLower || workReasons.length > 0;

  return {
    normalGross,
    actualGross,
    difference,
    workReasons,
    metrics,
    show,
    isHigher,
    isLower,
  };
}

export function categorizeDeductions(deductions = []) {
  const buckets = {
    pf: [],
    esi: [],
    pt: [],
    tds: [],
    loan: [],
    advance: [],
    lop: [],
    other: [],
  };

  for (const item of deductions || []) {
    const code = normCode(item);
    const name = String(item?.component || item?.name || "").toLowerCase();
    const amt = Number(item?.amount);
    if (!Number.isFinite(amt) || amt === 0) continue;

    if (code === "LOSS_OF_PAY" || /loss of pay|lop/i.test(name)) {
      buckets.lop.push(item);
    } else if (/^PF$|EPF|PROVIDENT/i.test(code) || /provident fund|^pf\b/i.test(name)) {
      buckets.pf.push(item);
    } else if (/ESI|ESIC/i.test(code) || /esi/i.test(name)) {
      buckets.esi.push(item);
    } else if (/^PT$|PROF/i.test(code) || /professional tax/i.test(name)) {
      buckets.pt.push(item);
    } else if (/TDS|INCOME.?TAX/i.test(code) || /tds|income tax/i.test(name)) {
      buckets.tds.push(item);
    } else if (/LOAN/i.test(code) || /loan/i.test(name)) {
      buckets.loan.push(item);
    } else if (/ADVANCE_TAKEN|ADVANCE/i.test(code) || /advance/i.test(name)) {
      buckets.advance.push(item);
    } else {
      buckets.other.push(item);
    }
  }

  return buckets;
}

export function flattenDeductionBuckets(buckets) {
  const order = ["pf", "esi", "pt", "tds", "loan", "advance", "lop", "other"];
  const rows = [];
  for (const key of order) {
    for (const item of buckets[key] || []) {
      rows.push(item);
    }
  }
  return rows;
}

export function buildStatutorySummary({ stat = {}, compliance = {} }) {
  const pfEmp = Number(stat.pf_contribution) || 0;
  const esiEmp = Number(stat.esi_contribution) || 0;
  const pt = Number(stat.pt) || 0;
  const tds = Number(stat.tds) || 0;
  const pfWage = compliance.pf_wage ?? stat.pf_wage ?? null;

  let employerPf = 0;
  for (const line of compliance.lines || []) {
    if (line.side === "employer" && /pf|epf|provident/i.test(String(line.label || line.code || ""))) {
      employerPf += Number(line.amount) || 0;
    }
  }

  const esiApplicable = esiEmp > 0 || Boolean(compliance.esic_registration_number);

  return {
    pfEmployee: pfEmp,
    pfEmployer: employerPf,
    pfWage,
    esiApplicable,
    esiEmployee: esiEmp,
    professionalTax: pt,
    tds,
  };
}

export function buildAuditMetadata(data = {}) {
  const audit = data.audit || {};
  const exec = data.execution_meta || data.pay_calculation?.audit || {};
  return {
    payroll_run_id: audit.payroll_run_id || data.payroll_run_id || null,
    payroll_version:
      audit.payroll_settings_fingerprint || exec.payroll_settings_fingerprint || exec.payroll_version || null,
    attendance_snapshot_id:
      audit.attendance_snapshot_id || exec.attendance_snapshot_id || exec.input_snapshot_id || null,
    salary_template_version: audit.salary_template_version || data.salary_package?.template_id || null,
    formula_version: audit.formula_version || exec.formula_version || exec.engine_version || "salary_engine_v2",
    processed_at: data.processed_at || null,
  };
}
